import { InputAdornment } from '@mui/material';
import { useCallback } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';

import {
  ExtendedTypeaheadQueryOpts,
  useAsyncTypeaheadProps,
} from '@/components/form/baseInputs/BackendTypeaheadSelectInput/hooks/useAsyncTypeaheadProps';
import {
  FormAwareBackendTypeaheadSelectInput,
  FormAwareBackendTypeaheadSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareBackendTypeaheadSelectInput';
import { User01Icon } from '@/components/icons/User01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { RelationshipOwner } from '@/modules/content/tooltipContent/RelationshipOwner';

import {
  GetPossibleRelationshipOwnersQuery,
  GetPossibleRelationshipOwnersQueryVariables,
  useGetPossibleRelationshipOwnersQuery,
} from './graphql/PossibleRelationshipOwnersTypeahead.generated';

export type PossibleRelationshipOwnersTypeahead<FormShape extends FieldValues> =
  Omit<
    FormAwareBackendTypeaheadSelectInputProps<FormShape, string>,
    'options' | 'inputValue' | 'onInputChange'
  > & {
    // Exclude a specific user ID from the results
    excludeUserId?: string;
  };

const EMPTY_OPTION = {
  value: '',
  groupName: '',
  display: EMPTY_CONTENT_HYPHEN,
};

export function PossibleRelationshipOwnersTypeahead<
  FormShape extends FieldValues,
>(props: PossibleRelationshipOwnersTypeahead<FormShape>) {
  const { excludeUserId } = props;
  const { createErrorFeedback } = useFeedback();
  const { control } = useFormContext<FormShape>();
  const selectedRelationshipOwnerId = useWatch({
    control,
    name: props.fieldName,
  });

  const searchTermToVariables = useCallback(
    (searchTerm: string) => {
      const baseFilter = {
        hasUserWith: [
          {
            or: [
              {
                searchNameContainsFold: searchTerm,
              },
              {
                emailContainsFold: searchTerm,
              },
            ],
          },
        ],
      };

      // If we need to exclude a user, add the exclusion filter
      if (excludeUserId) {
        return {
          optionsLike: {
            and: [
              baseFilter,
              {
                hasUserWith: [
                  {
                    idNEQ: excludeUserId,
                  },
                ],
              },
            ],
          },
        };
      }

      return {
        optionsLike: baseFilter,
      };
    },
    [excludeUserId]
  );

  const getSelectedOptionVariables = useCallback(() => {
    const baseSelectedFilter = {
      hasUserWith: [
        {
          id: selectedRelationshipOwnerId,
        },
      ],
    };

    // If we need to exclude a user, add the exclusion filter
    if (excludeUserId && selectedRelationshipOwnerId) {
      return {
        selectedOptionsLike: {
          and: [
            baseSelectedFilter,
            {
              hasUserWith: [
                {
                  idNEQ: excludeUserId,
                },
              ],
            },
          ],
        },
      };
    }

    return {
      selectedOptionsLike: baseSelectedFilter,
    };
  }, [selectedRelationshipOwnerId, excludeUserId]);

  const toOptions: ExtendedTypeaheadQueryOpts<
    string,
    GetPossibleRelationshipOwnersQuery,
    GetPossibleRelationshipOwnersQueryVariables
  >['toOptions'] = useCallback((data) => {
    return data.map((possibleRelationshipOwner) => ({
      // This query returns employees, but relationship-owner form fields use user ids.
      value: possibleRelationshipOwner.user.id,
      display: possibleRelationshipOwner.displayName,
    }));
  }, []);

  const [asyncTypeaheadProps] = useAsyncTypeaheadProps(
    useGetPossibleRelationshipOwnersQuery,
    {
      emptyOption: EMPTY_OPTION,
      pageSize: 50,
      searchTermToVariables,
      getSelectedOptionVariables,
      toOptions,
      onError: createErrorFeedback(
        'Error fetching clients. Please refresh the page to try again.'
      ),
    }
  );

  return (
    <FormAwareBackendTypeaheadSelectInput<FormShape, string>
      {...asyncTypeaheadProps}
      placeholder="Choose a relationship owner"
      contextualHelp={<RelationshipOwner />}
      startAdornment={
        <InputAdornment position="start">
          <User01Icon size={16} />
        </InputAdornment>
      }
      {...props}
    />
  );
}
